import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import { type FC, useEffect } from 'react';
import { MdChevronRight } from 'react-icons/md/index';
import { SELECT_INPUT_TEST_IDS } from './constants';
import InputError from '~/components/feedback/InputError';
import type { IRegionInput } from '~/types/inputs';
import { getInputSizeClasses } from '~/utils/form';
import '../Select/style.css';
import config from '~/config';

const options = {
	Australia: [
		{
			value: 'NSW',
			label: 'New South Wales',
		},
		{
			value: 'QLD',
			label: 'Queensland',
		},
		{
			value: 'SA',
			label: 'South Australia',
		},
		{
			value: 'TAS',
			label: 'Tasmania',
		},
		{
			value: 'VIC',
			label: 'Victoria',
		},
		{
			value: 'WA',
			label: 'Western Australia',
		},
		{
			value: 'ACT',
			label: 'Australian Capital Territory',
		},
		{
			value: 'NT',
			label: 'Northern Territory',
		},
	],
	'New Zealand': [
		{ value: 'Auckland', label: 'Auckland' },
		{ value: 'Bay of Plenty', label: 'Bay of Plenty' },
		{ value: 'Canterbury', label: 'Canterbury' },
		{ value: 'Gisborne', label: 'Gisborne' },
		{ value: "Hawke's Bay", label: "Hawke's Bay" },
		{ value: 'Marlborough', label: 'Marlborough' },
		{ value: 'Nelson - Tasman', label: 'Nelson - Tasman' },
		{ value: 'Northland', label: 'Northland' },
		{ value: 'Otago', label: 'Otago' },
		{ value: 'Southland', label: 'Southland' },
		{ value: 'Taranaki', label: 'Taranaki' },
		{ value: 'Waikato', label: 'Waikato' },
		{ value: 'Wellington', label: 'Wellington' },
		{ value: 'West Coast', label: 'West Coast' },
		{ value: 'Whanganui - Manawatu', label: 'Whanganui - Manawatu' },
	],
};

function useOptions() {
	const { values, setFieldValue, setFieldTouched } = useFormikContext<{
		country: 'Australia' | 'New Zealand' | '';
		state_or_region: string;
	}>();

	useEffect(() => {
		setFieldValue('state_or_region', '');
		setFieldTouched('state_or_region', false);
	}, [values.country]);

	if (!values.country) {
		return [
			{ value: '', label: 'Please select' },
			...options.Australia,
			...options['New Zealand'],
		];
	}

	return [
		{ value: '', label: 'Please select' },
		...options[values.country === 'New Zealand' ? 'New Zealand' : 'Australia'],
	];
}

const Region: FC<IRegionInput> = ({ name, label, width }) => {
	const [field, meta] = useField(name);
	const { submitCount } = useFormikContext();
	const options = useOptions();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;

	return (
		<div
			className={clsx(
				'select-input-container mx-auto rounded-tl-input rounded-tr-input relative h-input w-full',
				getInputSizeClasses(width),
			)}
			data-testid={SELECT_INPUT_TEST_IDS.ROOT}
		>
			<select
				{...field}
				className={clsx(
					'select-input-input peer cursor-pointer peer appearance-none bg-transparent placeholder-transparent text-base w-full outline-none pl-5 pr-5 pt-6 pb-1 h-full',
					field.value ? 'has-value' : '',
					isError ? 'is-error' : '',
				)}
				id={name}
				placeholder=" "
				data-testid={SELECT_INPUT_TEST_IDS.INPUT}
			>
				{options.map(option => {
					return (
						<option value={option.value} key={option.value}>
							{option.label}
						</option>
					);
				})}
			</select>
			<label
				htmlFor={name}
				className={clsx(
					'select-input-label pointer-events-none transition-all top-3 text-xs absolute left-5',
					field.value ? 'has-value' : '',
				)}
				data-testid={SELECT_INPUT_TEST_IDS.LABEL}
			>
				{label}
			</label>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="15.338"
				height="9.79"
				viewBox="0 0 15.338 9.79"
				className="select-input-chevron absolute top-1/2 right-5 pointer-events-none"
			>
				<path
					d="m2.121 2.121 5.548 5.548 5.548-5.548"
					fill="none"
					stroke="currentColor"
					strokeLinecap={config.areElementsRounded ? 'round' : 'butt'}
					strokeWidth="3"
					className="transition-colors duration-200"
				/>
			</svg>
			<div className="select-input-border absolute -bottom-[2px] left-0 transition-colors duration-200 w-full h-[2px]" />
			<InputError name={name} isError={isError}>
				{meta.error}
			</InputError>
		</div>
	);
};

export default Region;
